// Gán / đổi bảng giá cho một cấp dưới trực tiếp (docs/KE_HOACH_BANG_GIA_KHI_TAO_DON_VI.md §4.6, §10) — mở từ ngăn
// chi tiết cấp dưới. Có sẵn trong thư viện của mình, hoặc lập bảng mới (người gọi mở hộp lập bảng rồi mở lại hộp này
// với bảng vừa tạo). Máy chủ kiểm như gán ở trang Bảng giá: đúng loại, của cấp trên trực tiếp, CTV không vượt trần.

import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Plus } from 'lucide-react'
import { toast } from 'sonner'
import { post } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'

export function AssignPriceDialog({
  unit,
  current = null,
  options,
  initialId,
  canCreate,
  onCreateNew,
  onClose,
}: {
  unit: { id: string; name: string }
  /** Bảng đang dùng (đổi bảng) — null = chưa có (gán lần đầu) */
  current?: { code: string; name: string } | null
  /** Bảng đúng loại trong thư viện của mình */
  options: Array<{ id: string; code: string; name: string; unitCount: number }>
  initialId?: string
  canCreate: boolean
  onCreateNew: () => void
  onClose: () => void
}) {
  const qc = useQueryClient()
  const [id, setId] = useState(initialId ?? '')
  const [error, setError] = useState<string | null>(null)
  // Đổi bảng: bỏ bảng đang dùng khỏi danh sách
  const choices = current ? options.filter((o) => o.code !== current.code) : options
  const assign = useMutation({
    mutationFn: () => post('/price-lists/assign', { unitShopId: unit.id, priceListId: id }),
    onSuccess: () => {
      const l = options.find((o) => o.id === id)
      toast.success(`Đã ${current ? 'đổi sang' : 'gán'} ${l?.code ?? 'bảng giá'} cho ${unit.name}`)
      qc.invalidateQueries({ queryKey: ['network-units'] })
      qc.invalidateQueries({ queryKey: ['network-price-lists'] })
      qc.invalidateQueries({ queryKey: ['price-lists'] })
      onClose()
    },
    onError: (e) => setError((e as Error).message),
  })

  return (
    <Dialog open onOpenChange={(o) => !o && !assign.isPending && onClose()}>
      <DialogContent className="sm:max-w-md" {...(current ? {} : { 'aria-describedby': undefined })}>
        <DialogHeader className="pr-8">
          <DialogTitle>{current ? 'Đổi bảng giá' : 'Gán bảng giá'} cho {unit.name}</DialogTitle>
          {current && (
            <DialogDescription>
              Đang dùng: {current.name} ({current.code})
            </DialogDescription>
          )}
        </DialogHeader>
        <div className="space-y-3">
          {choices.length === 0 ? (
            <p className="text-sm text-muted">Chưa có bảng giá nào.</p>
          ) : (
            <select
              aria-label="Bảng giá"
              className="h-11 w-full rounded-md border border-rule bg-paper px-2 text-sm desktop:h-9"
              value={id}
              onChange={(e) => {
                setId(e.target.value)
                setError(null)
              }}
            >
              <option value="">— Chọn bảng giá —</option>
              {choices.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name} ({l.code}) · {l.unitCount} đơn vị đang dùng
                </option>
              ))}
            </select>
          )}
          {canCreate && (
            <Button type="button" variant="outline" className="gap-1.5" onClick={onCreateNew} disabled={assign.isPending}>
              <Plus className="size-4" aria-hidden />
              Tạo bảng giá
            </Button>
          )}
          {error && (
            <p role="alert" className="flex items-start gap-2 rounded-lg bg-danger/5 px-3 py-2 text-sm text-danger">
              <AlertTriangle className="mt-0.5 size-4 shrink-0" />
              {error}
            </p>
          )}
        </div>
        <DialogFooter className="gap-2 sm:space-x-0">
          <Button variant="outline" onClick={onClose} disabled={assign.isPending}>
            Hủy
          </Button>
          <Button onClick={() => assign.mutate()} disabled={!id || assign.isPending}>
            {assign.isPending ? 'Đang gán…' : 'Gán bảng'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
